const DB_NAME = 'mapstr-cache';
const DB_VERSION = 1;
const SCENE_STORE = 'scene-states';

// ==================== IndexedDB Helper ====================

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(SCENE_STORE)) {
        db.createObjectStore(SCENE_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(storeName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbGet(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function dbDelete(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbClear(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ==================== Scene State Cache ====================

export const sceneStateCache = {
  async save(sceneId, stateData) {
    if (!sceneId) return;
    try {
      await dbPut(SCENE_STORE, {
        id: sceneId,
        ...stateData,
        cached_at: Date.now(),
      });
    } catch (e) {
      console.warn('Scene state cache save failed:', e);
    }
  },

  async load(sceneId) {
    if (!sceneId) return null;
    try {
      return await dbGet(SCENE_STORE, sceneId);
    } catch (e) {
      console.warn('Scene state cache load failed:', e);
      return null;
    }
  },

  async remove(sceneId) {
    try { await dbDelete(SCENE_STORE, sceneId); } catch (e) { /* ignore */ }
  },

  async clear() {
    try { await dbClear(SCENE_STORE); } catch (e) { /* ignore */ }
  },
};

// ==================== Asset Preloader ====================

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const preloadingSet = new Set();

export const assetPreloader = {
  /**
   * Preload a list of file IDs into the Service Worker cache.
   * Uses low-priority fetch to avoid blocking the main thread.
   */
  preloadFiles(fileIds) {
    const uniqueIds = fileIds.filter(id => id && !preloadingSet.has(id));
    uniqueIds.forEach(id => {
      preloadingSet.add(id);
      const url = `${API}/files/${id}`;
      fetch(url, { priority: 'low' }).catch(() => {}).finally(() => {
        preloadingSet.delete(id);
      });
    });
  },

  /**
   * Preload all assets referenced by a scene object.
   */
  preloadScene(scene) {
    if (!scene) return;
    const fileIds = [];

    // Map file ID
    if (scene.map_url && !scene.map_url.startsWith('http')) {
      fileIds.push(scene.map_url);
    }

    // Token images
    (scene.placed_tokens || []).forEach(token => {
      if (token.image_url) {
        const match = token.image_url.match(/\/files\/([^/]+)$/);
        if (match) fileIds.push(match[1]);
      }
      (token.states || []).forEach(state => {
        if (state.file_id) fileIds.push(state.file_id);
      });
    });

    if (fileIds.length > 0) {
      this.preloadFiles(fileIds);
    }
  },

  /**
   * Preload assets for multiple scenes (e.g., adjacent scenes).
   */
  preloadScenes(scenes) {
    (scenes || []).forEach(sc => this.preloadScene(sc));
  },

  /**
   * Preload thumbnails for a list of file IDs.
   */
  preloadThumbnails(fileIds) {
    const uniqueIds = fileIds.filter(id => id && !preloadingSet.has(`thumb-${id}`));
    uniqueIds.forEach(id => {
      preloadingSet.add(`thumb-${id}`);
      fetch(`${API}/files/${id}/thumb`, { priority: 'low' }).catch(() => {}).finally(() => {
        preloadingSet.delete(`thumb-${id}`);
      });
    });
  },
};

// ==================== Service Worker Communication ====================

export function clearAssetCache() {
  return new Promise((resolve) => {
    if (!navigator.serviceWorker?.controller) {
      resolve(false);
      return;
    }
    const ch = new MessageChannel();
    ch.port1.onmessage = () => resolve(true);
    navigator.serviceWorker.controller.postMessage('CLEAR_CACHE', [ch.port2]);
    // Timeout fallback
    setTimeout(() => resolve(false), 3000);
  });
}

export function getCacheStats() {
  return new Promise((resolve) => {
    if (!navigator.serviceWorker?.controller) {
      resolve({ entries: 0 });
      return;
    }
    const ch = new MessageChannel();
    ch.port1.onmessage = (e) => resolve(e.data);
    navigator.serviceWorker.controller.postMessage('GET_CACHE_STATS', [ch.port2]);
    setTimeout(() => resolve({ entries: 0 }), 3000);
  });
}

// ==================== Register Service Worker ====================

export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').then(
        (reg) => console.log('[Mapstr] SW registered, scope:', reg.scope),
        (err) => console.warn('[Mapstr] SW registration failed:', err)
      );
    });
  }
}
